import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  RewardsState,
  PromoCodesResponse,
  DiscountsResponse,
} from "../../types/rewards";
import {
  getRewardsDashboardStats,
  getActiveRewards,
  getRewardRecentActivities,
  getWeeklyDiscountTrend,
  getWeeklyEngagementTrend,
  getAllPromoCodes,
  createPromoCode,
  getAllDiscounts,
  createCampaign,
  getAllCampaigns,
  updateCampaign,
  toggleCampaignStatus,
  deleteCampaign,
  createDiscount,
  updateDiscount,
  toggleDiscountStatus,
  deleteDiscount,
  createFreeDelivery,
  getAllFreeDeliveries,
  updateFreeDelivery,
  toggleFreeDeliveryStatus,
  deleteFreeDelivery,
  createPromoCodeFunction,
  getAllPromoCodeFunctions,
  updatePromoCodeFunction,
  deletePromoCodeFunction,
  createDiscountFunction,
  getAllDiscountFunctions,
  updateDiscountFunction,
  deleteDiscountFunction,
  createFreeDeliveryFunction,
  getAllFreeDeliveryFunctions,
  updateFreeDeliveryFunction,
  deleteFreeDeliveryFunction,
  getGamificationConfig,
  updateGamificationConfig,
  getAllCheckoutCategories,
  createCheckoutCategory,
  updateCheckoutCategory,
  deleteCheckoutCategory,
} from "../thunk";

const initialState: RewardsState = {
  stats: null,
  activeRewards: [],
  recentActivities: [],
  recentActivitiesPagination: undefined,
  recentActivitiesHasMore: true,
  weeklyDiscountTrend: null,
  weeklyEngagementTrend: null,
  promoCodes: [],
  discounts: [],
  campaigns: [],
  freeDeliveries: [],
  promoCodeFunctions: [],
  discountFunctions: [],
  freeDeliveryFunctions: [],
  checkoutCategories: [],
  gamificationConfig: null,
  loading: false,
  error: null,
};

const rewardsSlice = createSlice({
  name: "rewards",
  initialState,
  reducers: {
    clearRewardsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Dashboard Stats
      .addCase(getRewardsDashboardStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getRewardsDashboardStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload.data;
      })
      .addCase(getRewardsDashboardStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Active Rewards
      .addCase(getActiveRewards.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getActiveRewards.fulfilled, (state, action) => {
        state.loading = false;
        state.activeRewards = action.payload.data;
      })
      .addCase(getActiveRewards.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Recent Activities
      .addCase(getRewardRecentActivities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getRewardRecentActivities.fulfilled, (state, action) => {
        state.loading = false;
        const page = action.meta.arg?.page ?? 1;
        const limit = action.meta.arg?.limit ?? 10;
        const nextItems = action.payload?.data ?? [];

        state.recentActivitiesPagination = action.payload?.pagination;

        state.recentActivities = nextItems;

        if (action.payload?.pagination?.totalPages != null) {
          state.recentActivitiesHasMore = page < action.payload.pagination.totalPages;
        } else {
          state.recentActivitiesHasMore = Array.isArray(nextItems)
            ? nextItems.length >= limit
            : false;
        }
      })
      .addCase(getRewardRecentActivities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Weekly Discount Trend
      .addCase(getWeeklyDiscountTrend.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getWeeklyDiscountTrend.fulfilled, (state, action) => {
        state.loading = false;
        state.weeklyDiscountTrend = action.payload.data;
      })
      .addCase(getWeeklyDiscountTrend.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Weekly Engagement Trend
      .addCase(getWeeklyEngagementTrend.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getWeeklyEngagementTrend.fulfilled, (state, action) => {
        state.loading = false;
        state.weeklyEngagementTrend = action.payload.data;
      })
      .addCase(getWeeklyEngagementTrend.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Promo Codes
      .addCase(getAllPromoCodes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getAllPromoCodes.fulfilled,
        (state, action: PayloadAction<PromoCodesResponse>) => {
          state.loading = false;
          state.promoCodes = action.payload.data;
        },
      )
      .addCase(getAllPromoCodes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(createPromoCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPromoCode.fulfilled, (state, action) => {
        state.loading = false;
        state.promoCodes.unshift(action.payload.data);
      })
      .addCase(createPromoCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Discounts
      .addCase(getAllDiscounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getAllDiscounts.fulfilled,
        (state, action: PayloadAction<DiscountsResponse>) => {
          state.loading = false;
          state.discounts = action.payload.data;
        },
      )
      .addCase(getAllDiscounts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Campaigns
      .addCase(getAllCampaigns.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllCampaigns.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns = action.payload.data;
      })
      .addCase(getAllCampaigns.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(createCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns.unshift(action.payload.data);
      })
      .addCase(createCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(toggleCampaignStatus.fulfilled, (state, action) => {
        const index = state.campaigns.findIndex(
          (c) => c._id === action.payload.data._id,
        );
        if (index !== -1) {
          state.campaigns[index] = action.payload.data;
        }
      })

      .addCase(updateCampaign.fulfilled, (state, action) => {
        const index = state.campaigns.findIndex(
          (c) => c._id === action.payload.data._id,
        );
        if (index !== -1) {
          state.campaigns[index] = action.payload.data;
        }
      })

      .addCase(deleteCampaign.fulfilled, (state, action) => {
        state.campaigns = state.campaigns.filter(
          (c) => c._id !== action.payload,
        );
      })

      // Discounts Extensions
      .addCase(createDiscount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createDiscount.fulfilled, (state, action) => {
        state.loading = false;
        state.discounts.unshift(action.payload.data);
      })
      .addCase(createDiscount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(toggleDiscountStatus.fulfilled, (state, action) => {
        const index = state.discounts.findIndex(
          (d) => d._id === action.payload.data._id,
        );
        if (index !== -1) {
          state.discounts[index] = action.payload.data;
        }
      })

      .addCase(updateDiscount.fulfilled, (state, action) => {
        const index = state.discounts.findIndex(
          (d) => d._id === action.payload.data._id,
        );
        if (index !== -1) {
          state.discounts[index] = action.payload.data;
        }
      })

      .addCase(deleteDiscount.fulfilled, (state, action) => {
        state.discounts = state.discounts.filter(
          (d) => d._id !== action.payload,
        );
      })

      // Free Delivery Extensions
      .addCase(getAllFreeDeliveries.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllFreeDeliveries.fulfilled, (state, action) => {
        state.loading = false;
        state.freeDeliveries = action.payload.data;
      })
      .addCase(getAllFreeDeliveries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(createFreeDelivery.pending, (state) => {
        state.loading = true;
      })
      .addCase(createFreeDelivery.fulfilled, (state, action) => {
        state.loading = false;
        state.freeDeliveries.unshift(action.payload.data);
      })
      .addCase(createFreeDelivery.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(toggleFreeDeliveryStatus.fulfilled, (state, action) => {
        const index = state.freeDeliveries.findIndex(
          (fd) => fd._id === action.payload.data._id,
        );
        if (index !== -1) {
          state.freeDeliveries[index] = action.payload.data;
        }
      })

      .addCase(updateFreeDelivery.fulfilled, (state, action) => {
        const index = state.freeDeliveries.findIndex(
          (fd) => fd._id === action.payload.data._id,
        );
        if (index !== -1) {
          state.freeDeliveries[index] = action.payload.data;
        }
      })

      .addCase(deleteFreeDelivery.fulfilled, (state, action) => {
        state.freeDeliveries = state.freeDeliveries.filter(
          (fd) => fd._id !== action.payload,
        );
      })

      // Promo Code Functions
      .addCase(getAllPromoCodeFunctions.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllPromoCodeFunctions.fulfilled, (state, action) => {
        state.loading = false;
        state.promoCodeFunctions = action.payload.data;
      })
      .addCase(getAllPromoCodeFunctions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(createPromoCodeFunction.pending, (state) => {
        state.loading = true;
      })
      .addCase(createPromoCodeFunction.fulfilled, (state, action) => {
        state.loading = false;
        state.promoCodeFunctions.unshift(action.payload.data);
      })
      .addCase(createPromoCodeFunction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(updatePromoCodeFunction.fulfilled, (state, action) => {
        const index = state.promoCodeFunctions.findIndex(
          (pf) => pf._id === action.payload.data._id,
        );
        if (index !== -1) {
          state.promoCodeFunctions[index] = action.payload.data;
        }
      })

      .addCase(deletePromoCodeFunction.fulfilled, (state, action) => {
        state.promoCodeFunctions = state.promoCodeFunctions.filter(
          (pf) => pf._id !== action.payload,
        );
      })

      // Discount Functions
      .addCase(getAllDiscountFunctions.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllDiscountFunctions.fulfilled, (state, action) => {
        state.loading = false;
        state.discountFunctions = action.payload.data;
      })
      .addCase(getAllDiscountFunctions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createDiscountFunction.fulfilled, (state, action) => {
        state.discountFunctions.unshift(action.payload.data);
      })
      .addCase(updateDiscountFunction.fulfilled, (state, action) => {
        const index = state.discountFunctions.findIndex(
          (df) => df._id === action.payload.data._id,
        );
        if (index !== -1) {
          state.discountFunctions[index] = action.payload.data;
        }
      })
      .addCase(deleteDiscountFunction.fulfilled, (state, action) => {
        state.discountFunctions = state.discountFunctions.filter(
          (df) => df._id !== action.payload,
        );
      })

      // Free Delivery Functions
      .addCase(getAllFreeDeliveryFunctions.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllFreeDeliveryFunctions.fulfilled, (state, action) => {
        state.loading = false;
        state.freeDeliveryFunctions = action.payload.data;
      })
      .addCase(getAllFreeDeliveryFunctions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createFreeDeliveryFunction.fulfilled, (state, action) => {
        state.freeDeliveryFunctions.unshift(action.payload.data);
      })
      .addCase(updateFreeDeliveryFunction.fulfilled, (state, action) => {
        const index = state.freeDeliveryFunctions.findIndex(
          (fd) => fd._id === action.payload.data._id,
        );
        if (index !== -1) {
          state.freeDeliveryFunctions[index] = action.payload.data;
        }
      })
      .addCase(deleteFreeDeliveryFunction.fulfilled, (state, action) => {
        state.freeDeliveryFunctions = state.freeDeliveryFunctions.filter(
          (fd) => fd._id !== action.payload,
        );
      })
      // Gamification Config
      .addCase(getGamificationConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getGamificationConfig.fulfilled, (state, action) => {
        state.loading = false;
        state.gamificationConfig = action.payload.data;
      })
      .addCase(getGamificationConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateGamificationConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateGamificationConfig.fulfilled, (state, action) => {
        state.loading = false;
        if (state.gamificationConfig && action.payload.data) {
          state.gamificationConfig = {
            ...state.gamificationConfig,
            ...action.payload.data,
          };
        } else {
          state.gamificationConfig = action.payload.data;
        }
      })
      .addCase(updateGamificationConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Checkout Categories
      .addCase(getAllCheckoutCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllCheckoutCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.checkoutCategories = action.payload.data.items;
      })
      .addCase(getAllCheckoutCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(createCheckoutCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCheckoutCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.checkoutCategories.unshift(action.payload.data);
      })
      .addCase(createCheckoutCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(updateCheckoutCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCheckoutCategory.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.checkoutCategories.findIndex(
          (c) => c._id === action.payload.data._id,
        );
        if (index !== -1) {
          state.checkoutCategories[index] = action.payload.data;
        }
      })
      .addCase(updateCheckoutCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(deleteCheckoutCategory.fulfilled, (state, action) => {
        state.checkoutCategories = state.checkoutCategories.filter(
          (c) => c._id !== action.payload,
        );
      });
  },
});

export const { clearRewardsError } = rewardsSlice.actions;
export default rewardsSlice.reducer;
